import React from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { receiptDraftSendIn, receiptDraftUpdated } from "store/actions/receiptActions";
import { documentDraftHandled } from "store/actions/documentDraftActions";
//#endregion
//#region types
import { CurrencyEnums, StyleEnums } from "models/enums";
import { ReceiptFooterProps } from "models/propTypes/receiptPropTypes";
import { TranslationKeys } from "translations";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { AutomationRuleFormTypes, ReceiptFormTypes } from "models/formTypes";
import { AutomationRuleType } from "models/enums/automationRule";
//#endregion
//#region components
import { PageFooter, useFloatingMessages } from "components/common";
import { ReceiptMoreActions, useReceiptPermissions } from "components/receipt";
import {
  useReceiptService,
  useDocumentDraftService,
  useFileUploadService,
  useAutomationRuleService,
  useCurrencyService,
} from "serviceHooks";
import { useSaveCostForwardingMarking } from "components/costForwardingMarking";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

const ReceiptDraftFooter: React.FC<ReceiptFooterProps> = (props) => {
  const receiptService = useReceiptService();
  const automationRuleService = useAutomationRuleService();
  const documentDraftService = useDocumentDraftService();
  const fileUploadService = useFileUploadService();
  const { enqueueMessage } = useFloatingMessages();
  const { getExchangeRate } = useCurrencyService();

  const { reloadReceipt, markAsDone, disableSuccessMessages } = props;
  const dispatch = useDispatch();
  const loggedInPersonId = useSelector((state: AppState) => state.auth.loggedInUser.personId);
  const { getReceiptAdminPermissions } = useReceiptPermissions();
  const saveWithCostForwardingMarking = useSaveCostForwardingMarking("receipt");

  // Mirrors the Superkoll API's update rule: only the uploader or a receipt admin may save.
  // Company admins can see and send in other people's drafts, but saving would return 403.
  const canSaveReceiptDraft = props.receipt.createdByPersonId === loggedInPersonId || getReceiptAdminPermissions();

  const handleReceiptDocumentDraft = async (values: ReceiptFormTypes.ReceiptDraftTodo) => {
    if (props.receipt.documentDraftId || values.documentDraftId) {
      await receiptService.handleReceiptDocumentDraft({
        receiptId: props.receipt.id,
        documentDraftId: values.documentDraftId,
      });
    }
  };

  const prepareReceiptDraft = async (values: ReceiptFormTypes.ReceiptDraftTodo) => {
    const files = await fileUploadService.updateMissingAttachments(
      values.filesToUpload ?? [],
      (attachmentId: number | string) => {
        if (values.filesToUpload?.find((i) => i.id === attachmentId)?.isDocumentDraft && values.documentDraftId) {
          return documentDraftService.getAttachmentData(values.documentDraftId, attachmentId);
        } else {
          return receiptService.getAttachmentData(props.receipt.id, attachmentId);
        }
      }
    );

    return receiptService.getUpdatedReceiptDraft(props.receipt, { ...values, filesToUpload: files });
  };

  const acceptReceipt = async () => {
    return props.receiptFormSubmitHandler((values: ReceiptFormTypes.ReceiptDraftTodo) =>
      saveWithCostForwardingMarking(props.receipt.id, values, async () => {
        const receiptDraft = await prepareReceiptDraft(values);
        await handleReceiptDocumentDraft(values);
        await receiptService.sign(props.receipt.id, receiptDraft);
      })
    );
  };

  // Core replaces the receipt's images only when the update carries image data, so a save that keeps
  // at least one own image removes the rest through that replace. Removing the last one needs the
  // delete endpoint, called after the update succeeded so a rejected save deletes nothing. Only the
  // last-image case uses it: when an image is kept, the update has already replaced the ids.
  const deleteImagesIfNoneKept = async (keptFiles: ReceiptFormTypes.ReceiptUploadedFile[]) => {
    if (keptFiles.length > 0) return;

    const imageIds = (props.receipt.attachments ?? [])
      .map((attachment) => attachment.id)
      .filter((id): id is number => id !== undefined);
    await Promise.all(imageIds.map((id) => receiptService.deleteImage(props.receipt.id, id)));
  };

  // Soft validation: a draft may be saved half-filled; the full scheme only applies when sending in.
  // The underlag is only linked on send-in. Linking marks it handled in Core, and unlinking it after
  // a save would return it to the inbox while the receipt kept its copied images.
  // The saved receipt is fetched before the click resolves, so the buttons stay busy until the form
  // has reloaded and nothing typed in between is lost.
  const saveReceiptDraft = async () => {
    return props.receiptFormSubmitHandler(
      (values: ReceiptFormTypes.ReceiptDraftTodo) =>
        saveWithCostForwardingMarking(props.receipt.id, values, async () => {
          const ownFiles = values.filesToUpload?.filter((file) => !file.isDocumentDraft) ?? [];
          await receiptService.put(props.receipt.id, await prepareReceiptDraft({ ...values, filesToUpload: ownFiles }));
          await deleteImagesIfNoneKept(ownFiles);
          return receiptService.get(props.receipt.id);
        }),
      true
    );
  };

  const createAutomationRule = async () => {
    return (
      props.ruleCreateFormSubmitHandler &&
      props.ruleCreateFormSubmitHandler(async (values: ReceiptFormTypes.AutomationRuleCreate) => {
        const draftValues = {
          receiptCategoryId: values.receiptDraft.category?.id,
          dimensions: values.receiptDraft.dimensions ?? [],
        };
        const ruleValues: AutomationRuleFormTypes.AutomationRuleCreate = {
          type: AutomationRuleType.ReceiptDraft,
          receiptDraft: values.receiptDraft,
        };

        await automationRuleService.createReceiptDraftRule(ruleValues, props.receipt.id);

        if (await doesReceiptInRangeOfCreatedRule(props.receipt, values.receiptDraft)) {
          await receiptService.sign(
            props.receipt.id,
            receiptService.getUpdatedReceiptDraft(props.receipt, draftValues)
          );

          receiptAcceptSuccess();
          return;
        }

        showReceiptDraftTodo();
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AutomationRule.CreateSuccess),
          StyleEnums.DisplayType.Success
        );
      })
    );
  };

  const doesReceiptInRangeOfCreatedRule = async (
    receipt: WintSuperkollAPIModel.Receipt,
    rule: AutomationRuleFormTypes.CreateReceiptDraftRule
  ) => {
    const minAmount = rule.minimumAmount;
    const maxAmount = rule.maximumAmount;
    const exchangeRate =
      receipt.currency === CurrencyEnums.CurrencyCode.SEK
        ? 1
        : await getExchangeRate(receipt.currency ?? CurrencyEnums.CurrencyCode.SEK, receipt.dateTime);
    const receiptAmountInSek = receipt.amount * exchangeRate;
    return (
      (minAmount === undefined || minAmount! <= receiptAmountInSek) &&
      (maxAmount === undefined || maxAmount! >= receiptAmountInSek)
    );
  };

  const receiptAcceptSuccess = () => {
    reloadReceipt((r) => {
      dispatch(receiptDraftSendIn(r.id));
      if (r.documentDraftId) {
        dispatch(documentDraftHandled(r.documentDraftId));
      }
    });
  };

  // A save keeps the todo open, so it shows its own toast (the card disables footer success
  // messages) and updates the receipt in place. reloadTodo is not used here: it covers the card
  // with the loading overlay and plays the done animation, both of which read as if the draft had
  // been sent in.
  const receiptDraftSaveSuccess = (savedReceipt?: WintSuperkollAPIModel.Receipt) => {
    savedReceipt && dispatch(receiptDraftUpdated(savedReceipt));
    enqueueMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptSavedSuccessMessage),
      StyleEnums.DisplayType.Success
    );
  };

  const showRuleCreateView = async () => {
    props.onShowRuleCreateView && props.onShowRuleCreateView();
  };

  const showReceiptDraftTodo = async () => {
    props.onShowReceiptDraftTodo && props.onShowReceiptDraftTodo();
  };

  const getReceiptDraftActionButtons = (): ActionButton[] => {
    const buttons: ActionButton[] = [];

    if (props.showRuleCreateButton) {
      buttons.push({
        text: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.AutomationRule.ReceiptDraftCreateRuleButtonText
        ),
        onClick: showRuleCreateView,
        type: StyleEnums.DisplayType.Info,
      });
    }

    if (canSaveReceiptDraft) {
      buttons.push({
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDraft),
        onClick: saveReceiptDraft,
        secondary: true,
        onActionSuccess: receiptDraftSaveSuccess,
        testId: "saveReceiptDraftButton",
      });
    }

    buttons.push({
      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.SendIn),
      onClick: acceptReceipt,
      type: StyleEnums.DisplayType.Success,
      successMessage: TranslationKeys.Receipt.DraftSendSuccess,
      onActionSuccess: receiptAcceptSuccess,
    });

    return buttons;
  };

  const getRuleCreateActionButtons = (): ActionButton[] => {
    const buttons: ActionButton[] = [];

    buttons.push({
      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Cancel),
      onClick: showReceiptDraftTodo,
      type: StyleEnums.DisplayType.Danger,
    });

    buttons.push({
      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AutomationRule.CreateRule),
      onClick: createAutomationRule,
      type: StyleEnums.DisplayType.Info,
      successMessage: TranslationKeys.AutomationRule.CreateSuccess,
    });

    return buttons;
  };

  return (
    <PageFooter
      actions={!props.isRuleCreateView ? getReceiptDraftActionButtons() : getRuleCreateActionButtons()}
      moreActions={
        !props.isRuleCreateView ? (
          <ReceiptMoreActions receipt={props.receipt} reloadReceipt={reloadReceipt} markAsDone={markAsDone} />
        ) : undefined
      }
      disableSuccessMessages={disableSuccessMessages}
    />
  );
};

export default ReceiptDraftFooter;
